import React from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import * as mobxReact from 'mobx-react';
import TableLoadingRow from '../../global/tables/table-loading-row';
import { ListLayout } from '../../global/tables/table-list-layout';
import DefaultButton from '../../global/default-button/default-button';
import './users-list.scss';
import { UsersVM } from '../users.viewmodel';
import { IUserDetailsDTO } from '../../../api/users.dao';
import { SearchInput } from '../../global/search-input/search-input';
import { Paging } from '../../global/_paging/paging-container';
import { Permission } from '../../../api/roles.dao';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import OptionsButton from '../../global/options-button/options-button';
import MultiSelectionDropdown from '../../global/multi-selection-dropdown/multi-selection-dropdown';
import { UserListItem } from './_user-list-item/user-list-item';
import OeStaffFilterDropdown from './_oe-staff-filter-dropdown/_oe-staff-filter-dropdown';
import { EMPTY_SEARCH_RESULT, MORE_BUTTON_TEXT } from '../../../constants/common-labels.constants';

interface IProps {
    intl: IntlShape;
    vm: UsersVM;
}

const UsersList = injectIntl(mobxReact.observer((props: IProps) => {
    const { intl, vm } = props;

    const isEmpty = !vm.usersList?.length && !vm.isListLoading;

    const title = (
        intl.formatMessage(
            {
                id: 'oe.common.users',
                defaultMessage: 'Users',
                description: 'Label text'
            })
    );

    const noAccountsFilterPlaceholder = (
        <div className='multi-selection-dropdown__placeholder'>
            <div>
                <FormattedMessage
                    id='oe.account.notFound'
                    defaultMessage="You don't have any Accounts yet."
                    description='No accounts label text'
                />
            </div>
        </div>
    );

    const noRolesFilterPlaceholder = (
        <div className='multi-selection-dropdown__placeholder'>
            <div>
                <FormattedMessage
                    id='oe.roles.notFound'
                    defaultMessage='No Roles found.'
                    description='No roles label text'
                />
            </div>
        </div>
    );

    const thead = [
        {
            title: intl.formatMessage(
                {
                    id: 'oe.common.firstName',
                    description: 'Label text',
                    defaultMessage: 'First Name'
                }),
            width: '15%'
        },
        {
            title: intl.formatMessage(
                {
                    id: 'oe.common.lastName',
                    description: 'Label text',
                    defaultMessage: 'Last Name'
                }),
            width: '10%'
        },
        {
            title: intl.formatMessage(
                {
                    id: 'oe.common.email',
                    description: 'Label text',
                    defaultMessage: 'Email'
                }),
            width: '25%'
        },
        {
            title: (
                <MultiSelectionDropdown
                    entityName={intl.formatMessage({
                        id: 'oe.common.roles',
                        description: 'Label text',
                        defaultMessage: 'Roles'
                    })}
                    searchPlaceholder={intl.formatMessage({
                        id: 'oe.role.searchRoles',
                        description: 'Label text',
                        defaultMessage: 'Search Roles'
                    })}
                    noItemsPlaceholder={noRolesFilterPlaceholder}
                    onApply={vm.handleChangeRolesFilter}
                    onFetchItems={vm.fetchRolesList}
                />
            ),
            width: '10%'
        },
        {
            title: (
                <MultiSelectionDropdown
                    entityName={intl.formatMessage({
                        id: 'oe.common.accounts',
                        description: 'Label text',
                        defaultMessage: 'Accounts'
                    })}
                    noItemsPlaceholder={noAccountsFilterPlaceholder}
                    onApply={operatorsIds => vm.handleChangeAccountsFilter(operatorsIds)}
                    onFetchItems={vm.fetchAccountsList}
                    withBESearch={true}
                />
            ),
            width: '15%'
        },
        {
          title: (
            <OeStaffFilterDropdown
                    entityName={intl.formatMessage({
                        id: 'oe.users.oeStaff',
                        description: 'Label text',
                        defaultMessage: 'OE Staff'
                    })}
                    oeStaffFilter={vm.oeStaffFilter}
                    isListLoading={vm.isListLoading}
                    onChangeFilter={vm.onChangeOeStaffFilter}
              />
          ),
          width: '10%'
        },
        {
            title: intl.formatMessage(
                {
                    id: 'oe.common.status',
                    description: 'Label text',
                    defaultMessage: 'Status'
                }),
            width: '15%'
        },
        {
            title: intl.formatMessage(
                {
                    id: 'oe.users.createdDate',
                    description: 'Label text',
                    defaultMessage: 'Created Date & Time'
                }),
            width: '15%'
        },
        {
            title: intl.formatMessage(
                {
                    id: 'oe.users.lastLoginDate',
                    description: 'Label text',
                    defaultMessage: 'Last Login Date & Time'
                }),
            width: '15%'
        },
        {
            customNode:
                <div className='search-input-container'>
                    <SearchInput
                        placeholder={intl.formatMessage({
                            id: 'oe.common.searchUsers',
                            defaultMessage: 'Search',
                            description: 'Search placeholder'
                        })}
                        resetSearch={vm.resetSearchQuery}
                        onChange={(e, value) => vm.searchUsers(value)}
                        withResetSearchButton
                        withDebounce
                    />
                </div>
        }
    ];

    const usersOptions = [
        hasPermission(Permission.USER_TAB) && {
            label: intl.formatMessage({
                id: 'oe.users.downloadCSV',
                defaultMessage: 'Download All Users',
                description: 'Label text'
            }),
            action: vm.downloadUsersCSV
        }
    ].filter(Boolean);

    return (
        <div className='page-container'>
            <section className='top-page-title horizontal-row horizontal-row--with-bottom-margin'>
                <h1 className='text-ellipsis' data-test-id='users-list-title'>{title}</h1>
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        <DefaultButton
                            className='events-list__button-new'
                            modifier='fat'
                            text={intl.formatMessage(
                                {
                                    id: `oe.user.newUser`,
                                    description: 'New User button',
                                    defaultMessage: '{prefix}New User'
                                },
                                {
                                    prefix: '+ '
                                }
                            )}
                            onClick={() => vm.showUserForm({ title: 'New User' })}
                        />
                        {!!usersOptions.length && <OptionsButton
                            modifier={['fat', 'options']}
                            label={intl.formatMessage(MORE_BUTTON_TEXT)}
                            options={usersOptions}
                        />}
                    </div>
                </div>
            </section>
            <section className='users-list fixed-list-layout'>
                <div>
                    <ListLayout sortOptions={{
                        theads: thead
                    }}>
                        {!vm.isListLoading && !!vm.usersList?.length && vm.usersList.map((item: IUserDetailsDTO) => {
                            return <UserListItem
                                {...item}
                                key={item.username}
                                isSelected={vm.isUserEditFormShown(item.username)}
                                editUser={vm.editUser}
                                searchQuery={vm.searchQuery}
                            />;
                        })}
                        {vm.isListLoading && <TableLoadingRow colSpan={thead.length}/>}
                    </ListLayout>
                    {isEmpty &&
                        <div className='empty-list-message'>
                            {(vm.searchQuery || vm.selectedFilterAccounts || vm.selectedFilterRoles) && (
                                <>
                                    <FormattedMessage {...EMPTY_SEARCH_RESULT} />
                                </>
                            )}
                        </div>
                    }
                </div>
                {!vm.isListLoading && <Paging pagingVM={vm.paginationVM}/>}
            </section>
        </div>
    );
}));

export default UsersList;
